/**
 * Applique les commandes du document de design sans modifier la source.
 * Les outils, la palette et le nombre d'elements bornent les creations.
 * Les calques restent ordonnes apres chaque deplacement.
 */
import { TGDComponent, TGDComponentType, TGDDesign, TGDTool } from '../../../models/courses/graphicDesign';
import { normalizeHex } from './color';

/** Erreurs possibles lors d'une commande. */
export type TGDCommandError = 'toolLocked' | 'maxElements' | 'paletteLocked' | 'notFound';
/** Resultat d'une commande de document. */
export interface TGDCommandResult { design: TGDDesign; error?: TGDCommandError; createdId?: string }
/** Limites imposees par la lecon. */
export interface TGDConstraints {
  allowedTools: TGDTool[];
  lockedPalette?: string[];
  maxElements?: number;
}
/** Sens d'un deplacement de calque. */
export type TGDReorder = 'forward' | 'backward' | 'front' | 'back';

const denied = (design: TGDDesign, error: TGDCommandError): TGDCommandResult => ({ design, error });
const round = (value: number): number => Math.round(value * 100) / 100;
const normalizeRotation = (value: number): number => {
  const normalized = ((value % 360) + 360) % 360;
  return round(normalized > 180 ? normalized - 360 : normalized);
};
const colorsAllowed = (component: Pick<TGDComponent, 'fill' | 'stroke'>,
  constraints: TGDConstraints): boolean => isColorAllowed(component.fill, constraints)
    && (component.stroke === undefined || isColorAllowed(component.stroke, constraints));
const normalizeColors = <T extends Pick<TGDComponent, 'fill' | 'stroke'>>(component: T): T => ({
  ...component,
  fill: normalizeHex(component.fill)!,
  ...(component.stroke === undefined ? {} : { stroke: normalizeHex(component.stroke)! }),
});

/** Associe un type d'element a son outil de creation. */
export const toolForType = (type: TGDComponentType): TGDTool => type;

/** Trouve le prochain identifiant numerique libre. */
export const nextComponentId = (design: TGDDesign): string => {
  const max = design.components.reduce((highest, component) => {
    const match = /^c(\d+)$/.exec(component.id);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
  return `c${max + 1}`;
};

/** Accepte une couleur hexadecimale autorisee par la palette. */
export const isColorAllowed = (color: string, constraints: TGDConstraints): boolean => {
  const normalized = normalizeHex(color);
  if (!normalized) return false;
  return !constraints.lockedPalette || constraints.lockedPalette.some(entry =>
    normalizeHex(entry) === normalized);
};

/** Ajoute un element au-dessus des autres si les contraintes le permettent. */
export const addComponent = (design: TGDDesign, component: Omit<TGDComponent, 'id' | 'layer'>,
  constraints: TGDConstraints): TGDCommandResult => {
  if (!constraints.allowedTools.includes(toolForType(component.type))) return denied(design, 'toolLocked');
  if (design.components.length + 1 > (constraints.maxElements ?? Infinity)) {
    return denied(design, 'maxElements');
  }
  if (!colorsAllowed(component, constraints)) return denied(design, 'paletteLocked');
  const id = nextComponentId(design);
  const layer = Math.max(0, ...design.components.map(item => item.layer ?? 0)) + 1;
  const created: TGDComponent = { ...normalizeColors(component), id, layer };
  return { design: { ...design, components: [...design.components, created] }, createdId: id };
};

/** Modifie un element existant, y compris si son outil de creation est verrouille. */
export const updateComponent = (design: TGDDesign, id: string,
  patch: Partial<Omit<TGDComponent, 'id' | 'type'>>,
  constraints: TGDConstraints): TGDCommandResult => {
  const index = design.components.findIndex(component => component.id === id);
  if (index < 0) return denied(design, 'notFound');
  if ((patch.fill !== undefined && !isColorAllowed(patch.fill, constraints))
    || (patch.stroke !== undefined && !isColorAllowed(patch.stroke, constraints))) {
    return denied(design, 'paletteLocked');
  }
  const numbers: (keyof Omit<TGDComponent, 'id' | 'type'>)[] = [
    'x', 'y', 'width', 'height', 'rotation', 'fontSize', 'strokeWidth',
    'layer', 'sides', 'cornerRadius',
  ];
  const sanitized: Partial<TGDComponent> = { ...patch };
  for (const key of numbers) {
    const value = patch[key];
    if (typeof value !== 'number') continue;
    let next = round(value);
    if (key === 'width' || key === 'height') next = Math.max(4, next);
    if (key === 'fontSize') next = Math.max(8, Math.min(400, next));
    if (key === 'rotation') next = normalizeRotation(value);
    (sanitized as Record<string, unknown>)[key] = next;
  }
  if (patch.fill !== undefined) sanitized.fill = normalizeHex(patch.fill)!;
  if (patch.stroke !== undefined) sanitized.stroke = normalizeHex(patch.stroke)!;
  const components = design.components.map((component, position) =>
    position === index ? { ...component, ...sanitized } : component);
  return { design: { ...design, components } };
};

/** Supprime un element retrouve par son identifiant. */
export const removeComponent = (design: TGDDesign, id: string): TGDCommandResult => {
  if (!design.components.some(component => component.id === id)) return denied(design, 'notFound');
  return { design: { ...design, components: design.components.filter(component => component.id !== id) } };
};

/** Copie un element avec un decalage, sur le calque superieur. */
export const duplicateComponent = (design: TGDDesign, id: string, constraints: TGDConstraints,
  offset = 16): TGDCommandResult => {
  const component = design.components.find(item => item.id === id);
  if (!component) return denied(design, 'notFound');
  const { id: oldId, layer, ...copy } = component;
  void oldId;
  void layer;
  return addComponent(design, { ...copy, x: copy.x + offset, y: copy.y + offset }, constraints);
};

/** Deplace un calque et renumerote tous les calques dans l'ordre visuel. */
export const reorderComponent = (design: TGDDesign, id: string, move: TGDReorder): TGDCommandResult => {
  const sorted = design.components.map((component, index) => ({ component, index }))
    .sort((a, b) => (a.component.layer ?? 0) - (b.component.layer ?? 0) || a.index - b.index);
  const index = sorted.findIndex(item => item.component.id === id);
  if (index < 0) return denied(design, 'notFound');
  const target = move === 'front' ? sorted.length - 1 : move === 'back' ? 0
    : move === 'forward' ? Math.min(index + 1, sorted.length - 1) : Math.max(index - 1, 0);
  if (target === index) return { design };
  const reordered = [...sorted];
  const [item] = reordered.splice(index, 1);
  reordered.splice(target, 0, item);
  const components = reordered.map((entry, position) => ({ ...entry.component, layer: position + 1 }));
  return { design: { ...design, components } };
};

/** Change le fond seulement si sa couleur est valide et autorisee. */
export const setBackground = (design: TGDDesign, color: string,
  constraints: TGDConstraints): TGDCommandResult => {
  const normalized = normalizeHex(color);
  if (!normalized) return { design };
  if (!isColorAllowed(normalized, constraints)) return denied(design, 'paletteLocked');
  return { design: { ...design, background: normalized } };
};
